import { Box } from 'features/knowledgeDocumentViewer/components/viewer/shared/types'
import { useDocumentTabDataContext } from 'features/knowledgeDocumentViewer/contexts/DocumentTabDataContext'
import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import {
  DocumentViewerPage,
  FilterOption,
} from 'features/knowledgeDocumentViewer/types'
import {
  type BoundingBoxJsonItem,
  shouldForceHideDocumentBoxes,
} from 'features/knowledgeDocumentViewer/types/ocrViewer'
import { useMemo } from 'react'

function mapBoundingBoxesToViewerBoxes(
  list: BoundingBoxJsonItem[],
  dims: { w: number; h: number },
  pageIndex: DocumentViewerPage,
  confirmed: boolean
): Box[] {
  return list.map((item) => ({
    id: `${String(item.id)}-${pageIndex}`,
    confirmed,
    x: item.bounding_box.left * dims.w,
    y: item.bounding_box.top * dims.h,
    w: item.bounding_box.width * dims.w,
    h: item.bounding_box.height * dims.h,
    label: item.text ?? String(item.id),
    kind: 'text',
    groupId: item.groupId,
    type: item.type,
    linkedUnitId: item.linkedUnitId,
    assetId: item.assetId,
  }))
}

export function useViewerBoxes(
  dims: { w: number; h: number } | null,
  pageIndex: DocumentViewerPage,
  isLoading: boolean
): Box[] {
  const { filterOptions, assetTypeFilters, currentDocumentQuery } =
    useKnowledgeDocumentUXContext()
  const { showDocumentOcrBoxes } = useKnowledgeDocumentDataContext()
  const { boundingBoxDataState } = useDocumentTabDataContext()
  const forceHideDocumentBoxes =
    shouldForceHideDocumentBoxes(showDocumentOcrBoxes)

  const { confirmedList, unconfirmedList } = useMemo(() => {
    if (!dims || isLoading || (dims.w === 0 && dims.h === 0)) {
      return { confirmedList: [] as Box[], unconfirmedList: [] as Box[] }
    }

    const confirmed = boundingBoxDataState?.[pageIndex]?.confirmed ?? []
    const unconfirmed = boundingBoxDataState?.[pageIndex]?.unconfirmed ?? []

    return {
      confirmedList: mapBoundingBoxesToViewerBoxes(
        confirmed,
        dims,
        pageIndex,
        true
      ),
      unconfirmedList: mapBoundingBoxesToViewerBoxes(
        unconfirmed,
        dims,
        pageIndex,
        false
      ),
    }
  }, [boundingBoxDataState, dims, isLoading, pageIndex])

  return useMemo(() => {
    if (forceHideDocumentBoxes) {
      return []
    }

    let filteredList: Box[]

    if (filterOptions === FilterOption.ShowUnconfirmed) {
      filteredList = unconfirmedList
    } else if (filterOptions === FilterOption.ShowConfirmed) {
      filteredList = confirmedList
    } else if (filterOptions === FilterOption.RemoveBoxes) {
      return []
    } else {
      const confirmedIds = new Set(confirmedList.map((box) => box.id))
      filteredList = [
        ...confirmedList,
        ...unconfirmedList.filter((box) => !confirmedIds.has(box.id)),
      ]
    }

    if (assetTypeFilters.length > 0) {
      filteredList = filteredList.filter(
        (box) => box.type && assetTypeFilters.includes(box.type)
      )
    }

    if (currentDocumentQuery.trim() === '') {
      return filteredList
    }

    const searchLower = currentDocumentQuery.toLowerCase()
    return filteredList.filter((box) =>
      box.label?.toLowerCase().includes(searchLower)
    )
  }, [
    assetTypeFilters,
    confirmedList,
    currentDocumentQuery,
    filterOptions,
    forceHideDocumentBoxes,
    unconfirmedList,
  ])
}
